import React, { useState, useEffect, useContext } from 'react';
import createAuth0Client, { Auth0ClientOptions } from '@auth0/auth0-spa-js';
import axios from 'axios';
import { EEndpoint } from '../config/endpoint.config';
import { useDispatch } from 'react-redux';
import {
  loadCurrentUser,
  loadToken,
} from '../store/current-user/current-user.actions';
import {
  incrementLoading,
  decrementLoading,
  returnNotification,
} from '../store/render-controller/render-controller.actions';
import { ENotification, EErrorName } from '../domain/notification.interface';
import { httpTimeout } from '../config/http.config';
import { ERoutes, Routes } from '../assets/constants/routes';
import { matchPath } from 'react-router';
import { StorageKeys } from '../assets/constants/storage';

interface Auth0ProviderProps {
  children: React.ReactNode;
  domain: string;
  clientId: string;
  audience: string;
}

export const Auth0Context = React.createContext<any>({});
export const useAuth0 = () => useContext(Auth0Context);

export const Auth0Provider = ({
  children,
  domain,
  clientId,
  audience,
}: Auth0ProviderProps) => {
  const dispatch = useDispatch();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState();
  const [auth0Client, setAuth0Client] = useState<any>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const location = window.location.href.replace(
      process.env.REACT_APP_BASE_URL as string,
      '',
    );

    const path =
      location.indexOf('?') > 0
        ? location.substring(0, location.indexOf('?'))
        : location;

    console.log('Trying to match path ' + path);
    const route: any = Routes.find(r => matchPath(path, r));

    console.log('Found match ' + JSON.stringify(route));
    if (!route || !route.restricted) {
      setLoading(false);
      return;
    }

    const initOptions = {
      domain: domain,
      client_id: clientId,
      audience: audience,
      redirect_uri: `${process.env.REACT_APP_BASE_URL}${ERoutes.SESSION}?callback=${location}`,
      cacheLocation: 'localstorage',
    } as Auth0ClientOptions;

    createAuth0Client(initOptions).then(client => {
      console.log('Auth0 client created');
      setAuth0Client(client);
    });

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!auth0Client) {
      console.log('Auth0Client not found, leaving');
      return;
    }

    if (!loading) {
      console.log('Loading is false, leaving');
      return;
    }

    console.log('Authenticating');
    authenticate();

    // eslint-disable-next-line
  }, [loading, auth0Client]);

  const authenticate = async () => {
    if (window.location.search.includes('code=')) {
      console.log(
        'Auth 0 needs to handle the redirect ' + window.location.search,
      );
      console.log(window.location.href);
      await auth0Client.handleRedirectCallback();
    }

    const isAuth0Authenticated = await auth0Client.isAuthenticated();
    setIsAuthenticated(isAuth0Authenticated);
    if (!isAuth0Authenticated) {
      console.log('User is not authenticated, so leaving');
      setLoading(false);
      return;
    }

    const auth0User = await auth0Client.getUser();
    setUser(auth0User);

    const token = await auth0Client.getTokenSilently();
    console.log('Token is ' + JSON.stringify(token));
    dispatch(incrementLoading());
    localStorage.setItem(StorageKeys.session, 'true');
    dispatch(loadToken(token));

    try {
      const res = await axios({
        method: 'get',
        baseURL: process.env.REACT_APP_API_URL,
        url: EEndpoint.GET_CURRENT_USER,
        headers: { Authorization: `Bearer ${token}` },
        timeout: httpTimeout,
      });
      dispatch(loadCurrentUser(res.data));
    } catch (error) {
      if (!error.response) {
        const notification = {
          type: ENotification.ERROR,
          msg1: 'error.generic',
        };
        dispatch(returnNotification(notification));
      }

      if (
        error.response.status === 404 &&
        error.response.data.exceptionName === EErrorName.UserNotFoundException
      ) {
        const res = await axios({
          method: 'post',
          baseURL: process.env.REACT_APP_API_URL,
          url: EEndpoint.REGISTER_USER,
          headers: { Authorization: `Bearer ${token}` },
        });
        dispatch(loadCurrentUser(res.data));
      }
    }

    dispatch(decrementLoading());
    setLoading(false);
  };

  const loginWithRedirect = (...p: any) => auth0Client.loginWithRedirect(...p);
  const logout = (...p: any) => {
    auth0Client.logout(...p);
    localStorage.clear();
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        loginWithRedirect,
        logout,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
